import React, { useState, useEffect } from 'react';
import Section from '../components/Section';
import Button from '../components/Button';
import Map from '../components/Map';
import SEO from '../components/SEO';
import content from '../data/content.json';
import { trackEvent } from '../utils/analytics';

const Contact: React.FC = () => {
  const contact = content.contact;
  const details = contact.content.find(sec => sec.section === 'Contact Details');
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Record that the contact page was viewed (funnel step before a form submit).
  useEffect(() => {
    trackEvent('view_contact_page');
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus(null);

    const formData = new FormData(e.currentTarget);
    formData.append('access_key', process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setFormStatus('Takk! Din henvendelse er mottatt!');
        (e.target as HTMLFormElement).reset();
        // Successful submission = a lead. 'generate_lead' is a GA4 recommended
        // event, so it can be imported into Google Ads as a conversion later.
        trackEvent('generate_lead', { method: 'contact_form' });
      } else {
        setFormStatus('Noe gikk galt. Vennligst prøv igjen.');
      }
    } catch {
      setFormStatus('Noe gikk galt. Vennligst prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "LegalService",
      "name": "Advokatfirmaet Bruland",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Drammensveien 82c",
        "postalCode": "0271",
        "addressLocality": "Oslo",
        "addressCountry": "NO"
      },
      "telephone": "+4722535322",
      "email": "advokat@bruland.no"
    }
  };

  return (
    <>
      <SEO
        title="Kontakt oss - Ta kontakt med våre advokater"
        description="Kontakt Advokatfirmaet Bruland i Oslo. Ring oss på +47 225 35 322 eller send en e-post til advokat@bruland.no. Vi er her for å hjelpe deg."
        keywords="kontakt advokat Oslo, juridisk bistand, advokat telefon, kontakt advokatfirma"
        canonicalUrl="/kontakt"
        structuredData={structuredData}
      />
      {/* Page Header */}
      <Section
        title="Kontakt oss"
        subtitle="Et juridisk team som beskytter dine interesser"
        background="gray"
        spacing="md"
        centered
      />

      {/* Two-Column Layout */}
      <Section background="white" spacing="lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Contact Info */}
          <div>
            <div className="bg-gray-50 p-8 rounded-lg mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-serif">Kontaktinformasjon</h3>

              {/* Office Hours */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Åpningstider</h4>
                <p className="text-gray-600">Mandag-Fredag</p>
                <p className="text-gray-600">08:00-16:00</p>
              </div>

              {/* Address */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Adresse</h4>
                <p className="text-gray-600">Drammensveien 82c</p>
                <p className="text-gray-600">0271 OSLO</p>
              </div>

              {/* Contact Details */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Kontakt</h4>
                <p className="mb-2">
                  <a href={`tel:${details?.phone?.replace(/[\s()]/g, '')}`} className="text-primary hover:text-primary-dark">
                    {details?.phone}
                  </a>
                </p>
                <p>
                  <a href={`mailto:${details?.email}`} className="text-primary hover:text-primary-dark">
                    {details?.email}
                  </a>
                </p>
              </div>
            </div>

            {/* Interactive Map */}
            <Map address="Drammensveien 82c, 0271 Oslo" height="256px" />
          </div>

          {/* Right Column - Contact Form */}
          <div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-serif">Send oss en melding</h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Navn
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors outline-none"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    E-post
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors outline-none"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Melding
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors outline-none resize-none"
                    required
                  />
                </div>

                <Button type="submit" disabled={isSubmitting} fullWidth size="lg">
                  {isSubmitting ? 'Sender...' : 'Send melding'}
                </Button>
              </form>

              {/* Status Messages */}
              {formStatus && (
                <div className={`mt-4 p-4 rounded-lg border-l-4 ${
                  formStatus.includes('Takk')
                    ? 'bg-success/10 border-success text-success'
                    : 'bg-error/10 border-error text-error'
                }`}>
                  <p className="font-medium">{formStatus}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Contact;

